import { GroupSecurity, RelatedPersonRelationship } from '@quromedical/fhir-common';
import { RelatedPersonGetResponse } from '@quromedical/models';
import { first } from '@quromedical/utils';
import { PatientHeaderSection } from 'core/patient';
import { Tabs } from 'design-system';
import { useUserSession } from 'hooks/useUserSession';
import { NextOfKinApi } from 'integration/aggregate';
import { PatientDrawerScreenProps } from 'navigation';
import { usePatientId } from 'providers/PatientIdContext';
import React, { useEffect, useState } from 'react';
import { ScrollView } from 'react-native';
import { NotFoundScreen } from 'screens/NotFoundScreen';
import { strings } from 'strings';

import { NextOfKinChatTab, PatientChatTab, PatientVideoConsultTab } from './tabs';

const api = new NextOfKinApi();

export const PatientCommunicationScreen: React.FC<
  PatientDrawerScreenProps<'Communication'>
> = () => {
  const id = usePatientId();
  const session = useUserSession();
  const [relatedPerson, setRelatedPerson] = useState<RelatedPersonGetResponse[]>([]);
  const [hasChatPermissions, setHasChatPermissions] = useState(false);
  const [hasVideoPermissions, setHasVideoPermissions] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        return;
      }
      const { relatedPersons } = await api.getAllNextOfKin({
        patient: id,
        relationship: RelatedPersonRelationship.NextOfKin,
      });
      setRelatedPerson(relatedPersons);

      const hasChatReadPermissions = session.hasAny([GroupSecurity.CommunicationRead]);
      const hasChatCreatePermissions = session.hasAny([GroupSecurity.CommunicationCreate]);

      // Only show WhatsApp chat if user has both read and create permissions
      // TODO: If user has chat read, we should show chat without a text and send box
      setHasChatPermissions(hasChatCreatePermissions && hasChatReadPermissions);

      setHasVideoPermissions(
        session.hasAny([GroupSecurity.VideoConsultCreate, GroupSecurity.VideoConsultInvite])
      );
    };

    fetchData();
  }, [id, session]);

  if (!id) {
    return <NotFoundScreen />;
  }

  const relatedPersonId = first(relatedPerson)?.id;

  return (
    <ScrollView>
      <PatientHeaderSection id={id} />

      <Tabs.Group>
        <Tabs.Screen isHidden={!hasChatPermissions} title={strings.WhatsappChatPatient}>
          <PatientChatTab patientId={id} />
        </Tabs.Screen>

        <Tabs.Screen
          isHidden={!hasChatPermissions || !relatedPersonId}
          title={strings.WhatsappChatNextOfKin}
        >
          <NextOfKinChatTab nextOfKinId={relatedPersonId as string} patientId={id} />
        </Tabs.Screen>

        <Tabs.Screen isHidden={!hasVideoPermissions} title={strings.VideoConsult}>
          <PatientVideoConsultTab />
        </Tabs.Screen>
      </Tabs.Group>
    </ScrollView>
  );
};
